import type { PlayerBodySegment } from "./player-body-trail";
import type { Vector2 } from "./player-movement";

export interface TailClipSnake {
  readonly body: readonly PlayerBodySegment[];
  readonly directionRadians: number;
  readonly growth: number;
  readonly id: string;
  readonly level: number;
  readonly position: Vector2;
  readonly radius: number;
}

export interface TailClipConfig {
  readonly baseSegmentCount: number;
  readonly mouthForwardScale: number;
  readonly mouthRadiusScale: number;
}

export interface TailClipContact {
  readonly attackerId: string;
  readonly positions: readonly Vector2[];
  readonly victimId: string;
}

export interface BlockedBodyContact {
  readonly attacker: TailClipSnake;
  readonly attackerId: string;
  readonly position: Vector2;
  readonly radius: number;
  readonly victimId: string;
}

export interface BodyBounce {
  readonly directionRadians: number;
  readonly position: Vector2;
}

export const getSnakeMouthPosition = (
  snake: Pick<TailClipSnake, "directionRadians" | "position" | "radius">,
  config: TailClipConfig,
): Vector2 => ({
  x:
    snake.position.x +
    Math.cos(snake.directionRadians) * snake.radius * config.mouthForwardScale,
  y:
    snake.position.y +
    Math.sin(snake.directionRadians) * snake.radius * config.mouthForwardScale,
});

const squaredDistanceToSegment = (
  point: Vector2,
  start: Vector2,
  end: Vector2,
): number => {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;
  if (lengthSquared === 0) {
    const distanceX = point.x - start.x;
    const distanceY = point.y - start.y;
    return distanceX * distanceX + distanceY * distanceY;
  }
  const projection = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) /
        lengthSquared,
    ),
  );
  const closestX = start.x + deltaX * projection;
  const closestY = start.y + deltaY * projection;
  const distanceX = point.x - closestX;
  const distanceY = point.y - closestY;
  return distanceX * distanceX + distanceY * distanceY;
};

const headsContact = (first: TailClipSnake, second: TailClipSnake): boolean => {
  const radius = first.radius + second.radius;
  const deltaX = first.position.x - second.position.x;
  const deltaY = first.position.y - second.position.y;
  return deltaX * deltaX + deltaY * deltaY <= radius * radius;
};

const bodiesContact = (
  attacker: TailClipSnake,
  segment: PlayerBodySegment,
): boolean => {
  const radius = attacker.radius + segment.radius;
  const deltaX = attacker.position.x - segment.position.x;
  const deltaY = attacker.position.y - segment.position.y;
  return deltaX * deltaX + deltaY * deltaY <= radius * radius;
};

const orderedSnakes = (
  snakes: readonly TailClipSnake[],
): readonly TailClipSnake[] =>
  [...snakes].sort((first, second) => first.id.localeCompare(second.id));

export const findBlockedBodyContacts = (
  snakes: readonly TailClipSnake[],
  config: TailClipConfig,
): readonly BlockedBodyContact[] => {
  const ordered = orderedSnakes(snakes);
  const contacts: BlockedBodyContact[] = [];
  for (const attacker of ordered) {
    for (const victim of ordered) {
      if (attacker.id === victim.id || headsContact(attacker, victim)) {
        continue;
      }
      const solidSegments =
        attacker.level > victim.level
          ? victim.level === 1
            ? []
            : victim.body.slice(0, config.baseSegmentCount)
          : victim.body;
      const segment = solidSegments.find((candidate) =>
        bodiesContact(attacker, candidate),
      );
      if (segment !== undefined) {
        contacts.push({
          attacker,
          attackerId: attacker.id,
          position: { ...segment.position },
          radius: segment.radius,
          victimId: victim.id,
        });
        break;
      }
    }
  }
  return contacts;
};

export const resolveBodyBounce = (
  attacker: TailClipSnake,
  contact: Pick<BlockedBodyContact, "position" | "radius">,
  separationDistance: number,
): BodyBounce => {
  const deltaX = attacker.position.x - contact.position.x;
  const deltaY = attacker.position.y - contact.position.y;
  const distance = Math.hypot(deltaX, deltaY);
  const normal =
    distance === 0
      ? {
          x: -Math.cos(attacker.directionRadians),
          y: -Math.sin(attacker.directionRadians),
        }
      : { x: deltaX / distance, y: deltaY / distance };
  const targetDistance = attacker.radius + contact.radius + separationDistance;
  return {
    directionRadians: Math.atan2(normal.y, normal.x),
    position: {
      x: contact.position.x + normal.x * targetDistance,
      y: contact.position.y + normal.y * targetDistance,
    },
  };
};

export const findTailClipContacts = (
  snakes: readonly TailClipSnake[],
  previousMouths: ReadonlyMap<string, Vector2>,
  config: TailClipConfig,
): readonly TailClipContact[] => {
  const ordered = orderedSnakes(snakes);
  const contacts: TailClipContact[] = [];
  for (const attacker of ordered) {
    const mouth = getSnakeMouthPosition(attacker, config);
    const previousMouth = previousMouths.get(attacker.id) ?? mouth;
    for (const victim of ordered) {
      if (
        attacker.id === victim.id ||
        attacker.level <= victim.level ||
        headsContact(attacker, victim)
      ) {
        continue;
      }
      const clipTargets =
        victim.level === 1
          ? victim.body
          : victim.body.slice(config.baseSegmentCount);
      const positions = clipTargets
        .filter((segment) => {
          const contactRadius =
            attacker.radius * config.mouthRadiusScale + segment.radius;
          return (
            squaredDistanceToSegment(segment.position, previousMouth, mouth) <=
            contactRadius * contactRadius
          );
        })
        .map((segment) => ({ ...segment.position }));
      if (positions.length > 0) {
        contacts.push({
          attackerId: attacker.id,
          positions,
          victimId: victim.id,
        });
      }
    }
  }
  return contacts;
};
